import type { DecorationAnchor, InventoryItem, PlacedItem } from "@townsy/contracts";
import type { DecorationPreview } from "../scene/types";
import { fittingRotations, freeAnchors, sameRotation, storedItems, type DecorScope } from "./homeLogic";

/**
 * The owner's uncommitted placement choice in "My home" (docs/01 section 6: "Preview is visibly
 * uncommitted until the backend confirms"). The draft only ever drives a translucent ghost in the
 * renderer; it is cleared once the server confirms the placement, on a refusal, on Cancel and when
 * the manager closes.
 */
export interface PlacementDraft {
  anchorId: string | null;
  inventoryInstanceId: string | null;
  rotation: number | null;
}

export const EMPTY_DRAFT: PlacementDraft = { anchorId: null, inventoryInstanceId: null, rotation: null };

export interface DraftContext {
  anchors: readonly DecorationAnchor[];
  placed: readonly PlacedItem[];
  inventory: readonly InventoryItem[];
  scope: DecorScope;
}

/**
 * Keep a draft consistent with the latest home and inventory: a spot that got taken, an item that
 * is no longer in storage or does not fit, or a rotation the spot does not allow is dropped; the
 * rotation defaults to the first one the spot allows for that item.
 */
export function normalizeDraft(draft: PlacementDraft, ctx: DraftContext): PlacementDraft {
  const free = freeAnchors(ctx.anchors, ctx.placed, ctx.scope);
  const anchor = draft.anchorId ? (free.find((a) => a.id === draft.anchorId) ?? null) : null;
  if (!anchor) return EMPTY_DRAFT;
  const item = draft.inventoryInstanceId ? (storedItems(ctx.inventory).find((i) => i.inventoryInstanceId === draft.inventoryInstanceId) ?? null) : null;
  if (!item) return { anchorId: anchor.id, inventoryInstanceId: null, rotation: null };
  const rotations = fittingRotations(anchor, item.itemId);
  if (rotations.length === 0) return { anchorId: anchor.id, inventoryInstanceId: null, rotation: null };
  const rotation = draft.rotation !== null && rotations.some((r) => sameRotation(r, draft.rotation!)) ? draft.rotation : rotations[0]!;
  return { anchorId: anchor.id, inventoryInstanceId: item.inventoryInstanceId, rotation };
}

export function draftsEqual(a: PlacementDraft, b: PlacementDraft): boolean {
  return a.anchorId === b.anchorId && a.inventoryInstanceId === b.inventoryInstanceId && a.rotation === b.rotation;
}

/** The ghost for a complete draft (spot + stored item + allowed rotation), else null. */
export function draftPreview(propertyId: string, scope: DecorScope, draft: PlacementDraft, inventory: readonly InventoryItem[]): DecorationPreview | null {
  if (!draft.anchorId || !draft.inventoryInstanceId || draft.rotation === null) return null;
  const item = inventory.find((i) => i.inventoryInstanceId === draft.inventoryInstanceId);
  if (!item || item.placement !== null) return null;
  return { propertyId, scope, anchorId: draft.anchorId, itemId: item.itemId, rotation: draft.rotation };
}

/** What the renderer is asked to mark: the chosen spot, or every free spot while none is chosen. */
export function draftHighlight(
  propertyId: string,
  scope: DecorScope,
  draft: PlacementDraft,
  ctx: Pick<DraftContext, "anchors" | "placed">,
): { propertyId: string; scope: DecorScope; anchorIds: string[] } {
  const anchorIds = draft.anchorId ? [draft.anchorId] : freeAnchors(ctx.anchors, ctx.placed, scope).map((a) => a.id);
  return { propertyId, scope, anchorIds };
}

export interface PreviewSink {
  setDecorationPreview(preview: DecorationPreview | null): void;
  highlightAnchors(propertyId: string, scope: DecorScope, anchorIds: string[]): void;
}

/**
 * Pushes the ghost and the anchor highlight to the renderer only when they change. `epoch` is the
 * room the renderer currently shows: a room change rebuilds the scene (dropping any ghost), so a
 * new epoch re-sends the current state.
 */
export class PreviewSync {
  private shownPreview = "none";
  private shownHighlight = "none";
  private highlightScope: { propertyId: string; scope: DecorScope } | null = null;
  private epoch: string | null = null;

  constructor(private readonly sink: PreviewSink) {}

  show(preview: DecorationPreview | null, highlight: { propertyId: string; scope: DecorScope; anchorIds: string[] } | null, epoch: string): void {
    if (epoch !== this.epoch) {
      // The renderer rebuilt its room: nothing of ours is visible any more.
      this.epoch = epoch;
      this.shownPreview = "none";
      this.shownHighlight = "none";
      this.highlightScope = null;
    }
    const previewKey = preview ? JSON.stringify(preview) : "none";
    if (previewKey !== this.shownPreview) {
      this.sink.setDecorationPreview(preview);
      this.shownPreview = previewKey;
    }
    const highlightKey = highlight ? JSON.stringify(highlight) : "none";
    if (highlightKey !== this.shownHighlight) {
      const prev = this.highlightScope;
      if (prev && (!highlight || prev.propertyId !== highlight.propertyId || prev.scope !== highlight.scope)) {
        // Switching tabs (or closing): clear the other scope's markers first.
        this.sink.highlightAnchors(prev.propertyId, prev.scope, []);
        this.highlightScope = null;
      }
      if (highlight) {
        this.sink.highlightAnchors(highlight.propertyId, highlight.scope, highlight.anchorIds);
        this.highlightScope = { propertyId: highlight.propertyId, scope: highlight.scope };
      }
      this.shownHighlight = highlightKey;
    }
  }

  /** Remove the ghost and every marker (manager closed, placement confirmed or refused). */
  clear(): void {
    if (this.shownPreview !== "none") this.sink.setDecorationPreview(null);
    if (this.highlightScope) this.sink.highlightAnchors(this.highlightScope.propertyId, this.highlightScope.scope, []);
    this.shownPreview = "none";
    this.shownHighlight = "none";
    this.highlightScope = null;
  }
}
